import { z } from 'zod';
import { Id, Inr, IsoDate, PageQuery, QueryBool, Timestamp, VerticalKey } from './common.js';

/** Pranav's SECTIONS (constants.json). */
export const Section = z.enum(['A', 'P', 'B', 'C', 'D', 'E', 'F', 'O', 'G']);
export type Section = z.infer<typeof Section>;
export const Ball = z.enum(['them', 'us', 'shared']);
export const Tier = z.enum(['Growth', 'Scale', 'Enterprise', 'Pilot']);

export const Relationship = z.object({
  id: Id,
  contactId: Id,
  accountId: Id,
  verticalId: Id,
  verticalKey: VerticalKey,
  ownerUserId: Id,
  section: Section,
  subseg: z.string().nullable(),
  ball: Ball,
  /** Computed server-side from the log (recomputeTouches); never client-sent. */
  touches: z.number().int().nonnegative(),
  watch: IsoDate.nullable(),
  watchSetOn: IsoDate.nullable(),
  blocker: z.string().nullable(),
  dealValue: Inr.nullable(),
  currentTier: Tier.nullable(),
  renewalDate: IsoDate.nullable(),
  status: z.string().nullable(),
  next: z.string().nullable(),
  archived: z.boolean(),
  /** e.g. "education touched this person 2 days ago". */
  crossVerticalWarnings: z.array(z.string()),
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Relationship = z.infer<typeof Relationship>;

export const DueFilter = z.enum(['overdue', 'today', 'week', 'none']);
export const RelationshipListQuery = PageQuery.extend({
  vertical: VerticalKey.optional(),
  section: Section.optional(),
  owner: z.union([Id, z.literal('me')]).optional(),
  due: DueFilter.optional(),
  archived: QueryBool.optional(),
  q: z.string().trim().max(200).optional(),
});
export type RelationshipListQuery = z.infer<typeof RelationshipListQuery>;

const editable = {
  section: Section,
  subseg: z.string().max(40).nullable(),
  ball: Ball,
  watch: IsoDate.nullable(),
  blocker: z.string().max(80).nullable(),
  dealValue: Inr.nullable(),
  currentTier: Tier.nullable(),
  renewalDate: IsoDate.nullable(),
  status: z.string().max(2000).nullable(),
  next: z.string().max(2000).nullable(),
  archived: z.boolean(),
};

export const CreateRelationshipRequest = z
  .object({
    contactId: Id,
    accountId: Id,
    verticalId: Id,
    ownerUserId: Id,
    ...editable,
  })
  .partial({ subseg: true, watch: true, blocker: true, dealValue: true, currentTier: true, renewalDate: true, status: true, next: true, archived: true, ball: true })
  .strict();
export type CreateRelationshipRequest = z.infer<typeof CreateRelationshipRequest>;

/** Optimistic concurrency: server rejects with VERSION_CONFLICT (409) if version is stale. */
export const UpdateRelationshipRequest = z
  .object(editable)
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateRelationshipRequest = z.infer<typeof UpdateRelationshipRequest>;

export const ReassignRequest = z
  .object({
    toUserId: Id,
    reason: z.string().trim().max(500).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type ReassignRequest = z.infer<typeof ReassignRequest>;

export const ReassignResponse = z.object({
  relationship: Relationship,
  movedReminders: z.number().int().nonnegative(),
  movedDrafts: z.number().int().nonnegative(),
});
export type ReassignResponse = z.infer<typeof ReassignResponse>;

export const AssignmentHistoryItem = z.object({
  id: Id,
  relationshipId: Id,
  fromUserId: Id.nullable(),
  toUserId: Id,
  byUserId: Id,
  reason: z.string().nullable(),
  at: Timestamp,
});
export type AssignmentHistoryItem = z.infer<typeof AssignmentHistoryItem>;
